import React, { useState, useRef, useMemo, useLayoutEffect } from "react";
import type { ScheduledOrder } from "~/features/scheduled/types";
import type { ScheduledWindowEdit } from "~/features/scheduled/components/ScheduledOrderEditModal";
import { formatDateWithYear, formatDateTimeFromISO, formatWindowLabel, orDoubleDash, stripCountryCode } from "~/shared/utils/formatting";
import { OutcomeBadge, getOutcomeLabel } from "~/features/orders/components/OutcomeBadge";
import { ScheduledOrderEditModal } from "./ScheduledOrderEditModal";
import { InlineFilterDropdown } from "~/shared/components/InlineFilterDropdown";

type ScheduledOrderTableProps = Readonly<{
  orders: ScheduledOrder[];
  loading?: boolean;
  selected: Record<string, boolean>;
  allSelected: boolean;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  title: string;
  emptyMessage: string;
  cityFilter?: string[];
  onCityFilterChange?: (selected: string[]) => void;
  cityOptions?: string[];
  outcomeFilter?: string[];
  onOutcomeFilterChange?: (selected: string[]) => void;
  whiteGloveFilter?: string[];
  onWhiteGloveFilterChange?: (selected: string[]) => void;
  onUpdateOrder?: (orderId: string, updates: {
    outcome: string;
    confirmedDate: string;
    confirmedWindow: ScheduledWindowEdit | null;
    comments: string;
    notes: string;
  }) => void;
}>;

const whiteGloveLabel = (flag?: boolean) => flag ? 'Yes' : 'No';

export function ScheduledOrderTable({
  orders,
  loading = false,
  selected,
  allSelected,
  onToggle,
  onSelectAll,
  onClear,
  title,
  emptyMessage,
  cityFilter = [],
  onCityFilterChange,
  cityOptions,
  outcomeFilter = [],
  onOutcomeFilterChange,
  whiteGloveFilter = [],
  onWhiteGloveFilterChange,
  onUpdateOrder,
}: ScheduledOrderTableProps) {
  const [editingOrder, setEditingOrder] = useState<ScheduledOrder | null>(null);
  const [editingRowOffset, setEditingRowOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef<number | null>(null);

  // Extract unique filter options from all orders (unfiltered) so the dropdown stays populated
  const filterOptions = useMemo(() => {
    const outcomes = new Set<string>();
    const whiteGloves = new Set<string>();
    orders.forEach(o => {
      if (o.outcome) outcomes.add(o.outcome);
      whiteGloves.add(whiteGloveLabel(o.whiteGlove));
    });
    const alpha = (a: string, b: string) => a.localeCompare(b);
    return {
      outcomes: Array.from(outcomes).sort(alpha),
      cities: cityOptions && cityOptions.length > 0 ? cityOptions : orders.map(o => o.city).filter((c): c is string => Boolean(c)).sort(alpha),
      whiteGloves: Array.from(whiteGloves).sort(alpha),
    };
  }, [orders, cityOptions]);

  // Apply all column filters client-side (each table is independent)
  const displayedOrders = useMemo(() => {
    let result = orders;
    if (cityFilter.length > 0)
      result = result.filter(o => o.city && cityFilter.includes(o.city));
    if (outcomeFilter.length > 0)
      result = result.filter(o => o.outcome && outcomeFilter.includes(o.outcome));
    if (whiteGloveFilter.length > 0)
      result = result.filter(o => whiteGloveFilter.includes(whiteGloveLabel(o.whiteGlove)));
    return result;
  }, [orders, cityFilter, outcomeFilter, whiteGloveFilter]);

  const hasActiveFilter = outcomeFilter.length > 0 || cityFilter.length > 0 || whiteGloveFilter.length > 0;

  const handleEdit = (order: ScheduledOrder, rowEl: HTMLElement | null) => {
    // If clicking the same order that's already being edited, do nothing
    if (editingOrder?._id === order._id) return;

    // Save table scroll position before layout shifts
    const savedScrollTop = scrollWrapperRef.current?.scrollTop ?? 0;

    let offset = 0;
    if (rowEl && scrollWrapperRef.current && containerRef.current) {
      const rowRect = rowEl.getBoundingClientRect();
      const scrollRect = scrollWrapperRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const titleBarHeight = scrollRect.top - containerRect.top;
      const rowInContent = rowRect.top - scrollRect.top + scrollWrapperRef.current.scrollTop;
      offset = titleBarHeight + rowInContent;
    }

    pendingScrollRef.current = savedScrollTop;
    setEditingOrder(order);
    setEditingRowOffset(offset);
  };

  const handleClose = () => {
    setEditingOrder(null);
    setEditingRowOffset(0);
  };

  const handleSave = (updates: {
    outcome: string;
    confirmedDate: string;
    confirmedWindow: ScheduledWindowEdit | null;
    comments: string;
    notes: string;
  }) => {
    if (editingOrder && onUpdateOrder) {
      onUpdateOrder(editingOrder.orderId, updates);
    }
    setEditingOrder(null);
    setEditingRowOffset(0);
  };

  // Scroll the editing row into view after the panel opens
  useLayoutEffect(() => {
    if (!editingOrder || !scrollWrapperRef.current) return;

    const wrapper = scrollWrapperRef.current;
    const row = wrapper.querySelector(
      `tr[data-order-id="${editingOrder._id}"]`
    ) as HTMLElement | null;

    if (!row) {
      // Fallback: restore saved scroll position
      if (pendingScrollRef.current != null) {
        wrapper.scrollTop = pendingScrollRef.current;
      }
      pendingScrollRef.current = null;
      return;
    }

    // Position the row just below the sticky header
    const rowRect = row.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const thead = wrapper.querySelector('thead');
    const theadHeight = thead ? thead.getBoundingClientRect().height : 0;
    const rowTopInScroll = rowRect.top - wrapperRect.top + wrapper.scrollTop;

    wrapper.scrollTop = Math.max(0, rowTopInScroll - theadHeight - 8);
    pendingScrollRef.current = null;
  }, [editingOrder]);

  const isEmpty = displayedOrders.length === 0;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        gap: 10,
        flex: 1,
        minHeight: 0,
        width: '100%',
        minWidth: 0,
        alignItems: isEmpty && !editingOrder ? 'flex-start' : 'stretch',
      }}
    >
      <div
        className="table-container table-container--scroll-inner"
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          position: 'relative',
        }}
      >
        {loading && orders.length > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.7)',
              zIndex: 20,
            }}
          >
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
          </div>
        )}
        <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: 'var(--neutral-gray)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'var(--primary-blue)',
              color: 'white',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {displayedOrders.length}
          </span>
        </div>
        <div
          ref={scrollWrapperRef}
          className="table-scroll-wrapper"
          style={{ flex: isEmpty ? '0 0 auto' : 1, overflowY: isEmpty ? 'hidden' : 'auto', minHeight: 0, position: 'relative' }}
        >
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => (e.target.checked ? onSelectAll() : onClear())}
                    aria-label={`Select all ${title.toLowerCase()}`}
                  />
                </th>
                <th>Confirmed Date & Window</th>
                <th>Last Call Date & Time</th>
                <th>SM Number</th>
                <th>Patient</th>
                <th>Patient ID</th>
                <th>
                  <InlineFilterDropdown
                    label="Outcome"
                    options={filterOptions.outcomes}
                    selected={outcomeFilter}
                    onChange={onOutcomeFilterChange ?? (() => { })}
                    formatOption={getOutcomeLabel}
                  />
                </th>
                <th>Planned Delivery Date & Window</th>
                <th>
                  <InlineFilterDropdown
                    label="City"
                    options={filterOptions.cities}
                    selected={cityFilter}
                    onChange={onCityFilterChange ?? (() => { })}
                    searchable
                  />
                </th>
                <th>Postal</th>
                <th>Phone</th>
                <th>Alt Phone</th>
                <th>
                  <InlineFilterDropdown
                    label="White Glove"
                    options={filterOptions.whiteGloves}
                    selected={whiteGloveFilter}
                    onChange={onWhiteGloveFilterChange ?? (() => { })}
                  />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isEmpty && (
                <tr>
                  <td
                    colSpan={14}
                    style={{ textAlign: 'center', padding: 0, color: 'var(--text-muted)', borderBottom: 'none' }}
                  >
                    <div
                      style={{
                        minHeight: 120,
                        padding: '16px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {hasActiveFilter ? 'No orders match the selected filters' : emptyMessage}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {displayedOrders.map(o => {
                const lastCallDateTime = o.lastCalledAt ? formatDateTimeFromISO(o.lastCalledAt) : null;
                const isEditing = editingOrder?._id === o._id;
                return (
                  <tr
                    key={o._id}
                    data-order-id={o._id}
                    className={o.whiteGlove ? 'whiteglove-yes' : 'whiteglove-no'}
                    style={{
                      background: isEditing ? '#E6F2FF' : undefined,
                      borderLeft: isEditing ? '4px solid var(--primary-blue)' : undefined,
                    }}
                  >
                    <td>
                      <input type="checkbox" checked={!!selected[o._id]} onChange={() => onToggle(o._id)} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        <div>{orDoubleDash(o.confirmedDateAndWindow?.newDate ? formatDateWithYear(o.confirmedDateAndWindow.newDate) : null)}</div>
                        <div>{orDoubleDash(o.confirmedDateAndWindow?.window ? formatWindowLabel(o.confirmedDateAndWindow.window) : null)}</div>
                      </div>
                    </td>
                    <td>
                      {lastCallDateTime ? `${lastCallDateTime.date} ${lastCallDateTime.time}` : orDoubleDash(null)}
                    </td>
                    <td><strong>{o.orderId}</strong></td>
                    <td>{orDoubleDash(o.patientName)}</td>
                    <td>{orDoubleDash(o.patientId)}</td>
                    <td><OutcomeBadge outcome={o.outcome} /></td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        <div>{orDoubleDash(o.plannedDeliveryDateAndWindow?.newDate ? formatDateWithYear(o.plannedDeliveryDateAndWindow.newDate) : null)}</div>
                        <div>{orDoubleDash(formatWindowLabel(o.plannedDeliveryDateAndWindow?.window))}</div>
                      </div>
                    </td>
                    <td>{orDoubleDash(o.city)}</td>
                    <td>{orDoubleDash(o.postalCode)}</td>
                    <td>{orDoubleDash(stripCountryCode(o.phone))}</td>
                    <td>{orDoubleDash(stripCountryCode(o.altPhone))}</td>
                    <td>{o.whiteGlove ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        title="Edit"
                        className="secondary"
                        onClick={(e) => {
                          const row = (e.currentTarget as HTMLElement).closest('tr') as HTMLElement | null;
                          handleEdit(o, row);
                        }}
                        style={{ padding: '4px 8px', fontSize: 14 }}
                      >
                        ✏️
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          width: editingOrder ? '400px' : '0px',
          flexShrink: 0,
          overflow: 'hidden',
          minHeight: 0,
          display: 'flex',
        }}
      >
        {editingOrder && (
          <div style={{ width: '400px' }}>
            <ScheduledOrderEditModal
              key={editingOrder._id}
              order={editingOrder}
              onClose={handleClose}
              onSave={handleSave}
              tailTop={editingRowOffset}
            />
          </div>
        )}
      </div>
    </div>
  );
}
